import { useState, type FormEvent } from 'react';
import type { TaskItem, TaskState } from '@skolveckan/shared';
import { EmptyState } from '../components/EmptyState';
import { IconPlus } from '../components/Icons';
import { LessonModal } from '../components/LessonModal';
import { TaskRow } from '../components/TaskRow';
import { useApi } from '../hooks/useApi';
import { api } from '../lib/api';
import { subjectColors } from '../lib/subjects';

type Filter = 'todo' | 'done' | 'all';

export function TasksPage() {
  const [filter, setFilter] = useState<Filter>('todo');
  const [openLessonId, setOpenLessonId] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, TaskState>>({});
  const [newTitle, setNewTitle] = useState('');

  const list = useApi(() => api.tasks('week'), []);

  async function toggle(task: TaskItem) {
    const next: TaskState = task.state === 'done' ? 'todo' : 'done';
    setOverrides((prev) => ({ ...prev, [task.id]: next }));
    try {
      await api.setTaskState(task.id, next);
    } catch {
      setOverrides((prev) => ({ ...prev, [task.id]: task.state }));
    }
  }

  async function addTask(e: FormEvent) {
    e.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    setNewTitle('');
    await api.createTask({ title, dueDate: null, dueMin: null });
    list.reload();
  }

  const groups = (list.data?.groups ?? [])
    .map((group) => ({
      ...group,
      items: group.items
        .map((t) => ({ ...t, state: overrides[t.id] ?? t.state }))
        .filter((t) =>
          filter === 'all' ? true : filter === 'done' ? t.state === 'done' : t.state === 'todo',
        ),
    }))
    .filter((group) => group.items.length > 0);

  const progress = list.data?.progress;

  return (
    <>
      <header className="page__head">
        <h1>Uppgifter</h1>
        <div className="segmented" role="group" aria-label="Filtrera uppgifter">
          {(
            [
              ['todo', 'Att göra'],
              ['done', 'Klara'],
              ['all', 'Alla'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={filter === value ? 'segmented__on' : undefined}
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="page__columns">
        <section className="card panel panel--grow">
          {list.loading && <p className="muted">Hämtar uppgifter…</p>}

          {!list.loading && groups.length === 0 && (
            <EmptyState
              title={filter === 'done' ? 'Inget avbockat än' : 'Inget att göra just nu'}
              body="Uppgifter dyker upp här när läraren lägger in dem på en lektion. Du kan också lägga till egna."
            />
          )}

          {groups.map((group) => (
            <div key={group.key} className="taskgroup">
              <span className="label">{group.label}</span>
              <div className="tasklist">
                {group.items.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onToggle={toggle}
                    onOpenLesson={setOpenLessonId}
                  />
                ))}
              </div>
            </div>
          ))}
        </section>

        <div className="page__aside">
          <section className="card panel">
            <span className="label">Veckans framsteg</span>
            <p className="progresshead">
              <strong>{progress?.done ?? 0}</strong> av {progress?.total ?? 0} uppgifter klara
            </p>
            <div className="stack" style={{ gap: 9 }}>
              {(progress?.bySubject ?? []).map((s) => {
                const colors = subjectColors(s.colorKey);
                return (
                  <div key={s.subjectId} className="subjectbar">
                    <span className="subjectbar__name">{s.name}</span>
                    <span className="progress">
                      <span
                        className="progress__fill"
                        style={{
                          width: `${s.total ? Math.round((s.done / s.total) * 100) : 0}%`,
                          background: colors.line,
                        }}
                      />
                    </span>
                    <span className="subjectbar__count">
                      {s.done}/{s.total}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="card panel">
            <span className="label">Egna uppgifter</span>
            <p className="muted">
              Lägg till sådant som inte kommer från skolan — träning, packa väskan, ringa mormor.
            </p>
            <form className="addtask" onSubmit={addTask}>
              <label className="visually-hidden" htmlFor="new-task">
                Ny uppgift
              </label>
              <input
                id="new-task"
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Ny uppgift…"
              />
              <button type="submit" className="button button--primary" aria-label="Lägg till">
                <IconPlus />
              </button>
            </form>
          </section>
        </div>
      </div>

      <LessonModal lessonId={openLessonId} onClose={() => setOpenLessonId(null)} />
    </>
  );
}
